import { LatLngLiteral } from "leaflet";
import { CreateAlertFormValues, EditAlertFormValues } from "pages/Alert/types";
import {
    ConditionGroup,
    IOperationRuleAlertUser,
} from "stores/data/insightAlerts/insightAlertModel";
import { SensorAlertConfig } from "types/ISensorAlertConfig";

/**
 * Color options for Insight visualization
 * Maps priorityId to color hex codes
 */
export const INSIGHT_COLORS = {
    RED: { id: 1, name: "red", color: "#BF1F1F" },
    YELLOW: { id: 2, name: "yellow", color: "#EAB308" },
    BLUE: { id: 3, name: "blue", color: "#004C96" },
    ORANGE: { id: 4, name: "orange", color: "#D24900" },
} as const;

export type InsightColorId = 1 | 2 | 3 | 4;

export interface InsightColor {
    id: InsightColorId;
    name: string;
    color: string;
}

export const INSIGHT_COLOR_LIST: InsightColor[] = [
    INSIGHT_COLORS.RED,
    INSIGHT_COLORS.ORANGE,
    INSIGHT_COLORS.YELLOW,
    INSIGHT_COLORS.BLUE,
];

/**
 * Extended form values for Event creation
 * Extends CreateAlertFormValues with Event-specific fields
 * 
 * ## Unified Endpoint Notes
 * 
 * Uses the unified `/api/v1/event/create` endpoint.
 * 
 * **Fields NOT sent to the endpoint (handled by backend):**
 * - `severityId`: Hardcoded in backend
 * - `geofence`: For insights, use locationId instead
 * - `operationRuleLocationId`: Replaced by `locationId`
 * 
 * **Control flags mapping:**
 * - `block1Active` → `shouldCreateInsight`
 * - `block2Active` → `shouldCreateAlert`
 * 
 * @see src/services/EventService.types.ts for unified payload structure
 * @see src/pages/Event/utils/eventPayloadMapper.ts for field mapping
 */
export interface CreateEventFormValues extends CreateAlertFormValues {
    // Block 1: Insight preferences (maps to shouldCreateInsight)
    block1Active: boolean;
    dashboardId?: number;
    restrictionType: boolean; // true = Restrictive, false = Permissive
    insightColor: InsightColorId;
    resume?: string; // User-typed description sent to insight.resume

    // Block 2: Alert preferences (maps to shouldCreateAlert)
    block2Active: boolean;

    // Block 3: Sensor alert configuration (optional)
    sensorAlertConfig?: SensorAlertConfig;
}

/**
 * Extended form values for Event editing
 * Extends EditAlertFormValues with Event-specific fields
 * 
 * Used when editing an existing Event (Insight + optional Alert)
 */
export interface EditEventFormValues extends EditAlertFormValues {
    // Block 1: Insight preferences
    block1Active: boolean;
    dashboardId?: number; // Dashboard ID for validation when block1Active is true
    restrictionType: boolean; // true = Restrictive, false = Permissive
    insightColor: InsightColorId;
    resume?: string; // User-typed description sent to insight.resume

    // Block 2: Alert preferences
    block2Active: boolean;

    // Block 3: Sensor alert configuration (optional)
    sensorAlertConfig?: SensorAlertConfig;

    // IDs for existing resources
    eventInsightId?: number; // The Insight ID being edited
    eventAlertIds?: number[]; // Associated Alert IDs
}

/**
 * Data structure for Event being edited
 * Contains all information needed to populate the edit form
 */
export interface EventEditData {
    // Insight data
    insightId: number;
    insightName: string;
    locationId: number;
    locationName: string;
    dashboardId: number;
    dashboardName: string;
    conditions?: ConditionGroup[];
    restrictionType: boolean;
    priorityId: InsightColorId;
    resume?: string;

    // Location data
    geofence?: LatLngLiteral[];
    station?: {
        id: number;
        name: string;
        lat: number;
        lon: number;
        regionId: number;
    };

    // Associated alerts
    alerts?: {
        id: number;
        name: string;
    }[];
}

/**
 * Get initial values for Event edit form from existing event data
 */
export const getEventEditInitialValues = (
    eventData: EventEditData
): EditEventFormValues => ({
    name: eventData.insightName,
    alertType: "extreme_wind", // Default, will be overridden if alert exists
    channels: ["email", "push_notification", "whatsapp"],
    selectedUsers: [],
    customTitle: "",
    customDescription: "",
    resume: eventData.resume || "",
    minAdvanceTime: 60,
    maxAdvanceTime: 1440,
    minimumEventDuration: 60,
    receiptType: ["sendUpdates", "sendCancellations"],
    conditionGroups: eventData.conditions,
    geofence: eventData.geofence,
    locationId: eventData.locationId,
    dashboardId: eventData.dashboardId,
    insightId: eventData.insightId,

    // Event-specific fields
    block1Active: true, // Always true when editing an existing insight
    block2Active: (eventData.alerts?.length ?? 0) > 0,
    restrictionType: eventData.restrictionType ?? true,
    insightColor: eventData.priorityId ?? 1,
    eventInsightId: eventData.insightId,
    eventAlertIds: eventData.alerts?.map(a => a.id),
});

/**
 * Aliases for unified endpoint compatibility (T013)
 * These provide semantic clarity when working with the unified payload
 */
export type ShouldCreateInsight = boolean;
export type ShouldCreateAlert = boolean;

/**
 * Props for the ToggleBlock component
 */
export interface ToggleBlockProps {
    title: string;
    isActive: boolean;
    onToggle: (active: boolean) => void;
    badge?: React.ReactNode;
    icon?: React.ReactNode;
    children: React.ReactNode;
    isDisabled?: boolean;
    /** If true, hides the switch entirely (content is always visible) */
    hideSwitch?: boolean;
}

/**
 * Props for InsightPreferencesBlock component
 */
export interface InsightPreferencesBlockProps {
    values: CreateEventFormValues;
    errors: any;
    touched: any;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
    t: (key: string) => string;
    /** If true, hides the create dashboard button (used in edit mode) */
    isEditMode?: boolean;
    /** If true, dashboard selection is locked (e.g., creating event from a specific location) */
    isDashboardLocked?: boolean;
}

/**
 * Props for AlertPreferencesBlock component
 */
export interface AlertPreferencesBlockProps {
    values: CreateEventFormValues;
    errors: any;
    touched: any;
    handleChange: any;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
    t: (key: React.Key) => string;
    usersTableSelected: React.Key[];
    setUsersTableSelected: (keys: React.Key[]) => void;
    alertUsers: IOperationRuleAlertUser[];
}

/**
 * Props for EventPreferencesStep component (Step 3)
 */
export interface EventPreferencesStepProps {
    values: CreateEventFormValues;
    errors: any;
    touched: any;
    handleChange: any;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
    t: (key: React.Key) => string;
    usersTableSelected: React.Key[];
    setUsersTableSelected: (keys: React.Key[]) => void;
    alertUsers: IOperationRuleAlertUser[];
    /** If true, insight block is always active and cannot be toggled off */
    forceInsightEnabled?: boolean;
    /** If true, dashboard selection is locked (e.g., creating event from a specific location) */
    isDashboardLocked?: boolean;
}

/**
 * Payload for creating an Insight
 */
export interface InsightCreationPayload {
    name: string;
    resume?: string;
    conditionGroup: any;
    restrictionType: boolean;
    priorityId: number;
    // Location specification (ONE of these)
    locationId?: number;
    geofence?: LatLngLiteral[];
    // Dashboard association
    operationRuleId: number;
}

/**
 * Props for CreateDashboardModal component
 */
export interface CreateDashboardModalProps {
    isOpen: boolean;
    onClose: () => void;
    onDashboardCreated: (dashboardId: number) => void;
    t: (key: string) => string;
}

/**
 * Initial values for Event creation form
 * Preserves block1Active and block2Active if they already exist in the input
 */
export const getEventInitialValues = (
    alertInitialValues: CreateAlertFormValues & Partial<Pick<CreateEventFormValues, 'block1Active' | 'block2Active' | 'restrictionType' | 'insightColor'>>
): CreateEventFormValues => ({
    ...alertInitialValues,
    block1Active: alertInitialValues.block1Active ?? false, // FR-030: Blocks start OFF by default, but preserve if already set
    block2Active: alertInitialValues.block2Active ?? true, // "Quero ser alertado" starts ON by default
    dashboardId: alertInitialValues.dashboardId ?? undefined,
    restrictionType: alertInitialValues.restrictionType ?? true, // Default to Restrictive
    insightColor: alertInitialValues.insightColor ?? 3, // Default to Blue
    resume: (alertInitialValues as any).resume ?? "", // Preserve if already set, empty by default
});
